import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export const APPROVAL_QUEUE_KEY = ['approvals'] as const;

export function useApprovalQueue() {
  return useQuery({
    queryKey: APPROVAL_QUEUE_KEY,
    queryFn: () => api.listPendingApprovals(),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useApprovalDetail(id: string | null) {
  return useQuery({
    queryKey: ['approvals', id],
    queryFn: () => api.getApproval(id!),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useApproveMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      api.approveMessage(id, notes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: APPROVAL_QUEUE_KEY });
    },
  });
}

export function useRejectMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.rejectMessage(id, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: APPROVAL_QUEUE_KEY });
    },
  });
}

export function useEditMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      subject,
      body,
    }: {
      id: string;
      subject: string;
      body: string;
    }) => api.editMessage(id, subject, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: APPROVAL_QUEUE_KEY });
    },
  });
}

export function useRegenerateMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.regenerateMessage(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: APPROVAL_QUEUE_KEY });
    },
  });
}

export function useSuppressContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.suppressContact(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: APPROVAL_QUEUE_KEY });
    },
  });
}
